import React from "react";
import { API } from "aws-amplify";
import _ from "lodash";
// Material table
import MaterialTable from "material-table";
import Switch from "@material-ui/core/Switch";
// Material ui icons
import { tableIcons } from "../../constants/materialTableIcons";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { connect } from "react-redux";
// Actions
import { pushMessage } from "../../redux/actions/commonActions";
import {
  clearListOfCustomSlotTypes,
  generateSkillBuilderCode,
  listCustomSlotTypes
} from "../../redux/actions/customSlotsActions";
// Utils
import { serializeCustomSlotTypeValue } from "../../common/utils";

import {getEnvironmentName} from "../../utils"
const environment = getEnvironmentName()
const config = require("../../app_configuration").CONFIGURATION[environment];

class CustomSlotsTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      columns: [{ title: "Value", field: "value" }, { title: "ID (optional)", field: "id" }],
      slotValues: [],
      select: false,
      refreshTable: false
    };

    this.apiRequestFixedParams = {
      Version: config.RESPONSUM_API_VERSION,
      Api: config.RESPONSUM_API,
      AccountType: config.RESPONSUM_API_RESOURCE_TYPE.PRIVATE
    };
  }

  componentDidMount() {
    this.handleSlotValues();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.selectedSlot !== this.props.selectedSlot) {
      this.handleSlotValues();
      this.setState(prevState => ({
        ...prevState,
        refreshTable: !prevState.refreshTable
      }));
    }

    if (prevProps.listCustomSlotTypesResponse !== this.props.listCustomSlotTypesResponse) {
      this.handleSlotValues();
    }
  }

  handleSlotValues = () => {
    let slotValues = [];

    if (this.props.listCustomSlotTypesResponse && this.props.listCustomSlotTypesResponse.custom_slots) {
      slotValues = this.props.parseDbSlotValues(
        this.props.listCustomSlotTypesResponse.custom_slots[this.props.selectedSlot]
      );
    }

    this.setState(prevState => ({
      ...prevState,
      slotValues: slotValues
    }));
  };

  handleSwitch = () => {
    this.setState(prevState => ({
      ...prevState,
      select: !prevState.select
    }));
  };

  /**
   * Add value to existing custom slot type.
   * Besides the value, an optional custom ID for the value can be also defined.
   * Note that synonyms can be added only after the slot value has been created.
   */
  serializeCustomSlotTypeWithNewValue = (slotType, newValue) => {
    const { slotName, ID, slotValue } = slotType;
    let newSlotValueSerialized = newValue.value;
    if (newValue.id && newValue.id.length > 0) {
      newSlotValueSerialized = newSlotValueSerialized.concat(`{"${newValue.id}"}`);
    }
    slotValue.push(newSlotValueSerialized);

    return `custom_slot_type=${encodeURIComponent(slotName)}&custom_slot_id=${encodeURIComponent(
      ID
    )}&custom_slot_values=${encodeURIComponent(slotValue.join("\r\n"))}`;
  };

  addCustomSlotTypeValue = (slotType, newValue) => {
    const requestData = {
      body: {
        AccessToken: this.props.userAccessToken,
        FormParams: this.serializeCustomSlotTypeWithNewValue(slotType, newValue),
        ApiCall: "CreateCustomSlot", // this is the API call for creating AND updating custom slot types
        Skill: this.props.skillId,
        ...this.apiRequestFixedParams
      }
    };

    return API.post(config.AWS_API_NAME, "intent-slots/custom", requestData);
  };

  handleSlotTypeUpdateResponse = response => {
    this.props.reloadCustomSlotTypes();

    if (response.code === 200) {
      this.props.generateSkillBuilderCode();

      this.props.pushMessage({
        type: "success",
        message: "Custom slot type updated"
      });

      return this.props.resetSelectedCustomSlotType();
    }

    this.props.pushMessage({
      type: "error",
      message: response.message
    });
  };

  /**
   * Validate custom slot type value added or edited in the custom slot types values table.
   * ignoredValue is used to ignore the currently edited custom slot type value (this means the user can update the
   * currently edited value to the same value).
   * NB: Synonyms are handled and validated in another component.
   * @return {{valid: boolean, message: string}}
   */
  validateCustomSlotTypeValue = (slotTypeValue, slotTypeValues, ignoredValue = undefined) => {
    const result = { valid: true, message: "Value is valid" };
    const existingValues = [],
      existingCustomIds = [];

    for (const customSlotTypeValue of slotTypeValues) {
      if (ignoredValue && ignoredValue.value === customSlotTypeValue.value) {
        continue;
      }

      existingValues.push(customSlotTypeValue.value);

      // Check if custom ID exists
      if (customSlotTypeValue.id && customSlotTypeValue.id !== "") {
        existingCustomIds.push(customSlotTypeValue.id);
      }
    }

    // Check if new value empty
    if (slotTypeValue.length === 0) {
      result.valid = false;
      result.message = "Value cannot be empty";
      return result;
    }

    // Check for duplicate values
    if (existingValues.indexOf(slotTypeValue.value) !== -1) {
      result.valid = false;
      result.message = "Value is already in use";
      return result;
    }

    if (slotTypeValue.id && slotTypeValue.id !== "") {
      // Check for duplicate custom IDs
      if (existingCustomIds.indexOf(slotTypeValue.id) !== -1) {
        result.valid = false;
        result.message = "ID is already in use";
        return result;
      }
    }

    return result;
  };

  onTableRowAdd = (newData, customSlotTypeValues) => {
    newData.value = newData.value.trim();
    const validationResult = this.validateCustomSlotTypeValue(newData, customSlotTypeValues);

    if (!validationResult.valid) {
      this.props.pushMessage({
        type: "error",
        message: validationResult.message
      });

      return new Promise((resolve, reject) => reject());
    }

    this.props.pushMessage({
      type: "info",
      message: "Updating custom slot type"
    });
    const slotIdx = this.props.selectedSlot;

    return this.addCustomSlotTypeValue(this.props.listCustomSlotTypesResponse.custom_slots[slotIdx], newData)
      .then(this.handleSlotTypeUpdateResponse)
      .catch(err => {
        this.props.pushMessage({
          type: "error",
          message: "Could not update custom slot type"
        });
      });
  };

  serializeCustomSlotTypeWithoutDeletedValue = (slotType, valueToDelete) => {
    const { slotName, ID, slotValue } = slotType;

    const serializedValueToDelete = serializeCustomSlotTypeValue(
      valueToDelete.value,
      valueToDelete.synonyms,
      valueToDelete.id
    );
    const newSlotValues = slotValue.filter(value => value !== serializedValueToDelete);

    return `custom_slot_type=${encodeURIComponent(slotName)}&custom_slot_id=${encodeURIComponent(
      ID
    )}&custom_slot_values=${encodeURIComponent(newSlotValues.join("\r\n"))}`;
  };

  deleteCustomSlotTypeValue = (slotType, valueToDelete) => {
    const requestData = {
      body: {
        AccessToken: this.props.userAccessToken,
        FormParams: this.serializeCustomSlotTypeWithoutDeletedValue(slotType, valueToDelete),
        ApiCall: "CreateCustomSlot", // this is the API call for creating AND updating custom slot types
        Skill: this.props.skillId,
        ...this.apiRequestFixedParams
      }
    };

    return API.post(config.AWS_API_NAME, "intent-slots/custom", requestData);
  };

  serializeUpdatedCustomSlotType = (slotType, newValue, valueToReplace) => {
    const { slotName, ID, slotValue } = slotType;
    const valueToReplaceSerialized = serializeCustomSlotTypeValue(
      valueToReplace.value,
      valueToReplace.synonyms,
      valueToReplace.id
    );
    const newValueSerialized = serializeCustomSlotTypeValue(newValue.value, newValue.synonyms, newValue.id);
    const updatedSlotValues = _.cloneDeep(slotValue);
    const valueToReplaceIdx = updatedSlotValues.indexOf(valueToReplaceSerialized);
    updatedSlotValues[valueToReplaceIdx] = newValueSerialized;

    return `custom_slot_type=${encodeURIComponent(slotName)}&custom_slot_id=${encodeURIComponent(
      ID
    )}&custom_slot_values=${encodeURIComponent(updatedSlotValues.join("\r\n"))}`;
  };

  onTableRowDelete = dataToDelete => {
    this.props.pushMessage({
      type: "info",
      message: "Updating custom slot type"
    });
    const slotIdx = this.props.selectedSlot;

    return this.deleteCustomSlotTypeValue(this.props.listCustomSlotTypesResponse.custom_slots[slotIdx], dataToDelete)
      .then(this.handleSlotTypeUpdateResponse)
      .catch(err => {
        this.props.pushMessage({
          type: "error",
          message: "Could not update custom slot type"
        });
      });
  };

  updateCustomSlotTypeValue = (slotType, newData, oldData) => {
    const requestData = {
      body: {
        AccessToken: this.props.userAccessToken,
        FormParams: this.serializeUpdatedCustomSlotType(slotType, newData, oldData),
        ApiCall: "CreateCustomSlot", // this is the API call for creating AND updating custom slot types
        Skill: this.props.skillId,
        ...this.apiRequestFixedParams
      }
    };

    return API.post(config.AWS_API_NAME, "intent-slots/custom", requestData);
  };

  onTableRowUpdate = (newData, oldData, customSlotTypeValues) => {
    newData.value = newData.value.trim();
    const validationResult = this.validateCustomSlotTypeValue(newData, customSlotTypeValues, oldData);

    if (!validationResult.valid) {
      this.props.pushMessage({
        type: "error",
        message: validationResult.message
      });

      return new Promise((resolve, reject) => reject());
    }

    this.props.pushMessage({
      type: "info",
      message: "Updating custom slot type"
    });

    const slotIdx = this.props.selectedSlot;

    return this.updateCustomSlotTypeValue(
      this.props.listCustomSlotTypesResponse.custom_slots[slotIdx],
      newData,
      oldData
    )
      .then(this.handleSlotTypeUpdateResponse)
      .catch(err => {
        this.props.pushMessage({
          type: "error",
          message: "Could not update custom slot type"
        });
      });
  };

  renderCustomSlotValuesTable = (slotValues, refreshTable, handleSelectValue) => {
    return (
      <MaterialTable
        icons={tableIcons}
        title="Custom Slot Values"
        refreshTable={refreshTable}
        columns={this.state.columns}
        data={slotValues}
        onRowClick={(evt, selectedRow) => handleSelectValue(selectedRow)}
        options={{
          selection: this.state.select
        }}
        actions={
          this.state.select
            ? [
                {
                  tooltip: "Remove All Selected Custom Slots",
                  icon: DeleteOutlineIcon,
                  onClick: (evt, data) => alert("You want to delete " + data.length + " rows")
                }
              ]
            : null
        }
        editable={
          !this.state.select
            ? {
                onRowAdd: newData => this.onTableRowAdd(newData, slotValues),
                onRowUpdate: (newData, oldData) => this.onTableRowUpdate(newData, oldData, slotValues),
                onRowDelete: this.onTableRowDelete
              }
            : undefined
        }
      />
    );
  };

  render() {
    const { slotValues, refreshTable } = this.state;
    const { handleSelectValue } = this.props;

    return (
      <React.Fragment>
        {this.renderCustomSlotValuesTable(slotValues, refreshTable, handleSelectValue)}
        <Switch
          checked={this.state.select}
          onChange={this.handleSwitch}
          value="checkedA"
          inputProps={{ "aria-label": "secondary checkbox" }}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => {
  return {
    message: state.common.message,
    listCustomSlotTypesResponse: state.customSlots.listCustomSlotTypesResponse
  };
};

export default connect(
  mapStateToProps,
  {
    pushMessage,
    clearListOfCustomSlotTypes,
    listCustomSlotTypes,
    generateSkillBuilderCode
  }
)(CustomSlotsTable);
